import { useCallback, useEffect, useRef, useState } from "react";
import {
  uploadBlob, startIngest,
  type UploadedFile, type IngestStartResponse,
} from "../api";
import { Pill } from "./ai/Primitives";

/** Provisioning states share one shape: a tone pill and a plain sentence. */


/**
 * Ingest modal. Drag-drop OR file-picker → parallel uploads to /ingest/blob →
 * "Start ingest" → /ingest/start runs the pipeline on the executor and writes
 * the pages into the content store. The response carries the verdict.
 */

type FileState = "queued" | "uploading" | "uploaded" | "failed";

interface FileRow {
  file: File;
  filename: string;        // sanitized
  state: FileState;
  progress: number;        // 0-100
  uploaded?: UploadedFile;
  error?: string;
}


export function IngestModal({
  open, instance, onClose, onIngested,
}: {
  open: boolean;
  instance: string;
  onClose: () => void;
  onIngested: () => void;
}) {
  const [rows, setRows] = useState<FileRow[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [context, setContext] = useState("");
  const [githubUrl, setGithubUrl] = useState("");
  const [subtree, setSubtree] = useState(".");
  const [started, setStarted] = useState<IngestStartResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  // reset on open
  useEffect(() => {
    if (!open) {
      setRows([]); setStarted(null); setErr(null);
    }
  }, [open]);

  // The start call wrote the store; the pages are searchable when it answers.
  useEffect(() => {
    if (started) onIngested();
  }, [started, onIngested]);

  // add + auto-upload files
  const enqueue = useCallback(async (files: FileList | File[]) => {
    const list = Array.from(files);
    const startIdx = rows.length;
    const newRows: FileRow[] = list.map(f => ({
      file: f,
      filename: sanitizeFilename(f.name),
      state: "queued",
      progress: 0,
    }));
    setRows(prev => [...prev, ...newRows]);
    // Upload in parallel (capped to 4 concurrent to be gentle)
    const SEM = 4;
    let cursor = 0;
    const workers: Promise<void>[] = [];
    const doOne = async () => {
      while (true) {
        const myIdx = cursor++;
        if (myIdx >= list.length) return;
        const rowIdx = startIdx + myIdx;
        const row = newRows[myIdx];
        setRowState(rowIdx, { state: "uploading", progress: 10 });
        try {
          const up = await uploadBlob(instance, row.filename, row.file);
          setRowState(rowIdx, { state: "uploaded", progress: 100, uploaded: up });
        } catch (e) {
          setRowState(rowIdx, { state: "failed", error: (e as Error).message });
        }
      }
    };
    for (let i = 0; i < Math.min(SEM, list.length); i++) {
      workers.push(doOne());
    }
    await Promise.all(workers);
  }, [instance, rows.length]);

  const setRowState = (idx: number, patch: Partial<FileRow>) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };

  const submit = async () => {
    const uploaded = rows
      .map(r => r.uploaded)
      .filter((u): u is UploadedFile => u !== undefined);
    if (uploaded.length === 0 && !githubUrl.trim()) {
      setErr("Add files or a GitHub repository URL.");
      return;
    }
    setErr(null);
    try {
      const r = await startIngest(instance, uploaded, context.trim() || undefined,
        githubUrl.trim() || undefined, subtree.trim() || ".");
      setStarted(r);
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  if (!open) return null;

  const allDone = rows.length > 0 && rows.every(r => r.state === "uploaded" || r.state === "failed");
  const okCount = rows.filter(r => r.state === "uploaded").length;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-wide" onClick={e => e.stopPropagation()}>
        {!started ? (
          <UploadStage
            rows={rows}
            dragOver={dragOver} setDragOver={setDragOver}
            fileInput={fileInput}
            onPick={(files: FileList | null) => files && enqueue(files)}
            onSubmit={submit}
            onCancel={onClose}
            allDone={allDone}
            okCount={okCount}
            err={err}
            instance={instance}
            context={context}
            setContext={setContext}
            githubUrl={githubUrl} setGithubUrl={setGithubUrl}
            subtree={subtree} setSubtree={setSubtree}
          />
        ) : (
          <IngestProgressView
            started={started} err={err}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}


function UploadStage({
  rows, dragOver, setDragOver, fileInput, onPick,
  onSubmit, onCancel, allDone, okCount, err, instance,
  context, setContext, githubUrl, setGithubUrl, subtree, setSubtree,
}: any) {
  return (
    <>
      <h2 className="modal-title">Ingest into <code>{instance}</code></h2>
      <p className="modal-hint">
        Drop files below or click to pick. Each file is uploaded to R2
        as you add it. When you're done, click <strong>Start ingest</strong>{" "}
        to open a PR and run the canon pipeline.
      </p>

      <div
        className={`drop-zone ${dragOver ? "drag-over" : ""}`}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => {
          e.preventDefault(); setDragOver(false);
          if (e.dataTransfer.files.length) onPick(e.dataTransfer.files);
        }}
        onClick={() => fileInput.current?.click()}
      >
        <div>
          {dragOver ? "Drop to upload" : (
            rows.length > 0
              ? `${rows.length} file${rows.length === 1 ? "" : "s"} added — drop more or click to pick`
              : "Drop files here, or click to pick"
          )}
        </div>
        <input
          ref={fileInput} type="file" multiple
          style={{ display: "none" }}
          onChange={e => onPick(e.target.files)}
        />
      </div>

      {rows.length > 0 && (
        <div className="file-list">
          {rows.map((r: FileRow, i: number) => (
            <div key={i} className={`file-row file-${r.state}`}>
              <div className="file-name">{r.filename}</div>
              <div className="file-meta">
                {r.state === "uploading" && "Uploading…"}
                {r.state === "queued" && "Queued"}
                {r.state === "uploaded" && r.uploaded && (
                  <>{humanSize(r.uploaded.size)} · sha256 {r.uploaded.sha256.slice(0, 8)}</>
                )}
                {r.state === "failed" && (
                  <span className="error-inline">{r.error?.slice(0, 80)}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <label style={{ display: "block", margin: "14px 0 4px" }}>
        <div style={{ fontSize: 13, color: "var(--ink-2)", marginBottom: 4 }}>
          GitHub repository <span style={{ opacity: 0.7 }}>(optional — code ingestion; CI clones it and renders code + specs into reviewable corpus pages)</span>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            value={githubUrl}
            onChange={e => setGithubUrl(e.target.value)}
            placeholder="https://github.com/owner/repo"
            style={{
              flex: 2, boxSizing: "border-box", padding: "8px 10px",
              fontSize: 13, fontFamily: "inherit",
            }}
          />
          <input
            value={subtree}
            onChange={e => setSubtree(e.target.value)}
            placeholder="subtree (e.g. src)"
            title="Walk root inside the repo"
            style={{
              flex: 1, boxSizing: "border-box", padding: "8px 10px",
              fontSize: 13, fontFamily: "inherit",
            }}
          />
        </div>
      </label>

      <label style={{ display: "block", margin: "14px 0 4px" }}>
        <div style={{ fontSize: 13, color: "var(--ink-2)", marginBottom: 4 }}>
          Ingest guidance <span style={{ opacity: 0.7 }}>(optional)</span>
        </div>
        <textarea
          value={context}
          onChange={e => setContext(e.target.value)}
          rows={2}
          placeholder="Steer extraction for this run — e.g. “this is a partner roster; one entity per row” or “focus on policy thresholds & effective dates”."
          style={{
            width: "100%", boxSizing: "border-box", padding: "8px 10px",
            fontSize: 13, resize: "vertical", fontFamily: "inherit",
          }}
        />
      </label>

      {err && <div className="error-banner">{err}</div>}

      <div className="modal-actions">
        <button onClick={onCancel}>Cancel</button>
        <button
          className="primary"
          onClick={onSubmit}
          disabled={rows.length > 0 ? (!allDone || okCount === 0) : !githubUrl.trim()}
        >
          Start ingest{okCount > 0 ? ` (${okCount} file${okCount === 1 ? "" : "s"})` : ""}
        </button>
      </div>
    </>
  );
}


function IngestProgressView({
  started, err, onClose,
}: {
  started: IngestStartResponse;
  err: string | null;
  onClose: () => void;
}) {
  // `unknown` means the run outlasted the request that dispatched it — still
  // deriving, not failed, and not repeated.
  const done = started.verdict === "ok";
  return (
    <>
      <h2 className="modal-title">
        Ingesting <code>{started.instance}</code> ({started.files_count} file{started.files_count === 1 ? "" : "s"})
      </h2>
      <div className="provision-status">
        <div className="provision-state">
          <Pill tone={done ? "done" : "running"} dot={!done}>
            {done ? "Ingested" : "Deriving"}
          </Pill>
          <span>{done
            ? `${started.written ?? 0} page${started.written === 1 ? "" : "s"} written.`
            : "Running on the executor."}</span>
        </div>
      </div>
      {err && <div className="error-banner">{err}</div>}
      <div className="modal-actions">
        <button onClick={onClose}>{done ? "Close" : "Run in background"}</button>
      </div>
    </>
  );
}


/* ---------- helpers ---------- */

function sanitizeFilename(name: string): string {
  // Strip leading/trailing whitespace, keep extension, normalize path-y
  // chars. Anchored to forbid `..` and absolute paths.
  return name.trim()
    .replace(/^\/+/, "")
    .replace(/\.\.+/g, ".")
    .replace(/\s+/g, "_");
}

function humanSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
